import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight } from "@fortawesome/pro-solid-svg-icons";

import { StatusCode } from "api/protocols";

import { showError } from "redux/actions/ui";

import { useAppDispatch } from "hooks/redux";
import { LocaleCurrency } from "helpers/Locale";
import { displayCurrency } from "helpers/Helpers";
import { formatGiftCardExpiryDate } from "helpers/giftCardExpiry";
import { GiftCardStatusBadge } from "pages/secure/facility/order/GiftCardsTable";

import { ButtonNew as Button } from "components/buttonNew";
import Spin from "components/spin/spin";
import Checkbox from "components/form/checkbox/Checkbox";
import Sheet from "components/sheet/Sheet";
import { Select } from "components/select/index";
import Popup from "components/popup/Popup";
import { Badge } from "components/badge/Badge";

import "./giftCardMergeTable.scss";
import { IGiftCard, MergeGiftCards } from "api/rpc/2024-04/facilityAdmin/payment/giftCard";

type GiftCardMergeTableProps = {
  giftCards: IGiftCard[];
  /** Returns the updated parent gift card. */
  mergeActionCallback?: (giftCard: IGiftCard) => void;
  /** Optional functionality for clicking a gift card row. */
  tableRowClick?: (giftCard: IGiftCard) => void;

  /** Displays Footer.  Gives access to table pagination. */
  footer?: false;

  hideTableOptions?: boolean;
};

type GiftCardMergeTablePropsWithFooter = {
  giftCards: IGiftCard[];
  hideTableOptions?: boolean;

  /** Returns the updated parent gift card. */
  mergeActionCallback?: (giftCard: IGiftCard) => void;
  /** Optional functionality for clicking a gift card row. */
  tableRowClick?: (giftCard: IGiftCard) => void;

  /** Gives access to table pagination. */
  footer: true;
  tableLimit: number;
  tableOffset: number;
  /** Allow parent to handle gift card pagination. Called when table footer buttons clicked. */
  handleTableOffset: (direction: "prev" | "next") => void;
};

const TABLE_COLUMN_COUNT = 6;
export default function GiftCardMergeTable(props: GiftCardMergeTableProps | GiftCardMergeTablePropsWithFooter) {
  const { t } = useTranslation();
  const { Option } = Select;
  const dispatch = useAppDispatch();

  const [checkboxState, setCheckboxState] = useState<number[]>([]);
  const [selectedGiftCardsState, setSelectedGiftCardsState] = useState<IGiftCard[]>([]);
  const [mergeModal, setMergeModal] = useState({
    visible: false,
    parentId: null,
    confirmationPopup: false,
  });

  const cardBuffer = props?.giftCards?.filter(card => checkboxState.includes(card.id) && card);

  /** @param [parent=false] Optional param to toggle every checkbox. */
  function handleChangeEvent(event: React.ChangeEvent<HTMLInputElement>, parent = false, giftCard?: IGiftCard) {
    event.stopPropagation();

    const targetId = Number(event.target.value);

    // Table Row checkbox click
    const currentGiftCardIDState: Array<number> = [...checkboxState];

    // Title checkbox click.  Return early.
    if (parent) {
      // When selecting all
      if (event.target.checked) {
        const allUnselectedGiftCards = props.giftCards
          .filter(value => value.status !== "disabled")
          .filter(value => !checkboxState.includes(value.id));
        const allUnselectedGiftCardIDs = allUnselectedGiftCards.map(card => card.id);

        setCheckboxState(prev => [...prev, ...allUnselectedGiftCardIDs]);
        setSelectedGiftCardsState(prev => [...prev, ...allUnselectedGiftCards]);
      } else {
        // When removing all
        const allSelectedGiftCards = props.giftCards
          .filter(value => value.status !== "disabled")
          .filter(value => checkboxState.includes(value.id));
        const allSelectedGiftCardIDs = allSelectedGiftCards.map(card => card.id);

        setCheckboxState(prev => prev.filter(id => !allSelectedGiftCardIDs.includes(id)));
        setSelectedGiftCardsState(prev =>
          prev.filter(card => !allSelectedGiftCards.some(selectedCard => card.id === selectedCard.id)),
        );
      }
      return;
    }

    const selectedGiftCardArray: Array<IGiftCard> = [...selectedGiftCardsState];
    const foundTargetIndex = checkboxState.indexOf(targetId);

    // If found, remove gift card otherwise remove gift card
    if (foundTargetIndex > -1) {
      currentGiftCardIDState.splice(foundTargetIndex, 1);
      selectedGiftCardArray.splice(foundTargetIndex, 1);
    } else {
      currentGiftCardIDState.push(targetId);
      selectedGiftCardArray.push(giftCard);
    }

    setSelectedGiftCardsState(selectedGiftCardArray);
    setCheckboxState(currentGiftCardIDState);
  }

  function handleClearSelections() {
    setCheckboxState([]);
    setSelectedGiftCardsState([]);
  }

  async function mergeCreditCardBalances() {
    const params = {
      parent_gift_card_id: mergeModal.parentId,
      gift_card_ids: selectedGiftCardsState
        .filter(value => value.id !== mergeModal.parentId) //remove the parent ID
        .map(card => card.id),
    };

    const res = await MergeGiftCards(params, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error merging credit card balances.")));
      return;
    }

    setMergeModal({ visible: false, parentId: null, confirmationPopup: false });
    setCheckboxState([]);
    setSelectedGiftCardsState([]);

    if (props.mergeActionCallback) {
      props.mergeActionCallback(res.data as IGiftCard);
    }
  }

  function handleTableRowClick(event: React.MouseEvent<HTMLTableRowElement, MouseEvent>, card: IGiftCard) {
    // Only apply callback if TR itself is clicked
    if ((event.target as HTMLElement).nodeName === "TD") {
      props.tableRowClick(card);
    }
  }

  return (
    <div className="table-wrap">
      <table className="giftcard-table ui-table clickable">
        <colgroup>
          <col style={{ width: "8%" }} />
          <col style={{ width: "24%" }} />
          <col style={{ width: "16%" }} />
          <col style={{ width: "12%" }} />
          <col style={{ width: "20%" }} />
          <col style={{ width: "20%" }} />
        </colgroup>

        <thead>
          <tr>
            <th colSpan={TABLE_COLUMN_COUNT - 2} />
            <th colSpan={2}>
              <div className="flex justify-evenly">
                {props.hideTableOptions ? null : (
                  <>
                    <Button
                      size="small"
                      onClick={() => setMergeModal(prev => ({ ...prev, visible: true }))}
                      disabled={!selectedGiftCardsState || selectedGiftCardsState?.length < 2}
                    >
                      {t("Merge Cards")}
                    </Button>

                    <Button
                      size="small"
                      onClick={handleClearSelections}
                      disabled={!selectedGiftCardsState || selectedGiftCardsState?.length < 2}
                    >
                      {t("Clear Selections")}
                    </Button>
                  </>
                )}
              </div>
            </th>
          </tr>
          <tr className="table-row-title">
            <th className="table-header-checkbox">
              {/* Only checked if all non-disabled boxes checked */}
              <Checkbox
                size="small"
                checked={
                  cardBuffer?.length === props?.giftCards?.filter(data => !data.status.includes("disabled"))?.length
                }
                onChange={event => handleChangeEvent(event, true)}
              />
            </th>
            <th className="table-header-giftcard">{t("Gift Card No.")}</th>
            <th className="table-header-balance">{t("Balance")}</th>
            <th className="table-header-currency">{t("Currency")}</th>
            <th className="table-header-expiry">{t("Expiry Date")}</th>
            <th className="table-header-status">{t("Status")}</th>
          </tr>
        </thead>

        <tbody>
          {props.giftCards ? (
            <>
              {props.giftCards.length !== 0 ? (
                <>
                  {props.giftCards.map(giftCard => {
                    return (
                      <tr
                        key={giftCard.id}
                        onClick={event => (props.tableRowClick ? handleTableRowClick(event, giftCard) : undefined)}
                      >
                        <td
                          id="checkbox"
                          className={`table-data-checkbox${giftCard.status.includes("disabled") ? " inactive" : ""}`}
                        >
                          <Checkbox
                            id={"checkbox_" + String(giftCard.id)}
                            size="small"
                            value={String(giftCard.id)}
                            checked={checkboxState.includes(giftCard.id) ?? false}
                            disabled={giftCard.status.includes("disabled")}
                            onChange={event => handleChangeEvent(event, false, giftCard)}
                          />
                        </td>
                        <td className="table-data-giftCard">{`**** **** **** ${giftCard.last4}`}</td>
                        <td className="table-data-balance">
                          <LocaleCurrency currency={giftCard.currency} amount={giftCard.balance} />
                        </td>
                        <td className="table-data-currency">{giftCard.currency.toUpperCase()}</td>
                        <td className="table-data-expiry">
                          {formatGiftCardExpiryDate(giftCard.expires_at) ?? t("No Expiration")}
                        </td>
                        <td className="table-data-status">
                          {giftCard.status.includes("disabled") ? (
                            <Badge type="warning">{t("Invalid")}</Badge>
                          ) : (
                            <GiftCardStatusBadge status={giftCard.status} expiresAt={giftCard.expires_at} />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </>
              ) : null}
            </>
          ) : (
            <tr id="gift-card-table-spinner">
              <td colSpan={TABLE_COLUMN_COUNT} height={"75px"} style={{ pointerEvents: "none" }}>
                <Spin />
              </td>
            </tr>
          )}
        </tbody>

        {props.footer && (
          <tfoot>
            <tr>
              <td colSpan={2}>
                <Button
                  size="small"
                  type="secondary"
                  onClick={() => {
                    props.handleTableOffset("prev");
                  }}
                  disabled={!(props.tableLimit <= props.tableOffset)}
                >
                  <FontAwesomeIcon icon={faArrowLeft} />
                  &nbsp;
                  {t("Previous")}
                </Button>
              </td>

              <td colSpan={TABLE_COLUMN_COUNT - 4} />

              <td colSpan={2}>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    size="small"
                    type="secondary"
                    onClick={() => {
                      props.handleTableOffset("next");
                    }}
                    disabled={!(props.giftCards?.length === props.tableLimit)}
                  >
                    {t("Next")}
                    &nbsp;
                    <FontAwesomeIcon icon={faArrowRight} />
                  </Button>
                </div>
              </td>
            </tr>
          </tfoot>
        )}
      </table>

      {/** Merge Modal + Popup */}
      {mergeModal.visible && (
        <Sheet
          open={mergeModal.visible}
          title={t("Choose The Gift Card Parent")}
          size="small"
          okText={`Merge ${calculateSumOfValues(selectedGiftCardsState)}`} /** TODO: Translations */
          onOk={() => setMergeModal(prev => ({ ...prev, confirmationPopup: true }))}
          okDisabled={mergeModal.parentId === null || mergeModal.parentId === -1 || mergeModal.confirmationPopup} // Limit Modal use when confirmation popup is open
          onCancel={() => setMergeModal({ visible: false, parentId: -1, confirmationPopup: false })}
          cancelDisabled={mergeModal.confirmationPopup} // Limit Modal use when confirmation popup is open
          cancelText={t("Cancel")}
        >
          <Select
            onChange={(value: number) => setMergeModal(prev => ({ ...prev, parentId: value }))}
            defaultValue={-1}
            className="parent-select"
          >
            <Option value={-1}>{t("Select Parent Card")}</Option>
            {selectedGiftCardsState &&
              selectedGiftCardsState.map(card => {
                return (
                  <Option key={card.id} value={card.id} name={card.last4}>
                    **** **** **** {card.last4}
                  </Option>
                );
              })}
          </Select>
        </Sheet>
      )}

      {/* Confirms the async Merge Credit Card call. */}
      <Popup
        open={mergeModal.confirmationPopup}
        type={"warning"}
        title={t("Merging Gift Card Balances")}
        description={
          t("Are you sure you want to load all the balances onto a single card?  This operation cannot be undone.")
        }
        onOk={mergeCreditCardBalances}
        okText={t("Merge Balances")}
        onCancel={() => setMergeModal(prev => ({ ...prev, confirmationPopup: false }))}
        cancelText={t("Cancel")}
      />
    </div>
  );
}

/**
 * Returns the sum of the all gift card balances as a currency string.
 */
function calculateSumOfValues(checkedCards: IGiftCard[]) {
  if (!checkedCards[0].currency) {
    return "";
  }

  let sum = 0;
  checkedCards?.forEach(card => {
    sum += card.balance;
  });

  return displayCurrency(checkedCards[0].currency, sum);
}
